import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Upload } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { LoadingState } from '@/components/ui/states';
import { Table, TD, TH, THead, TR } from '@/components/ui/table';
import { apiError } from './lib';

interface ImportRow {
  line: number;
  name: string;
  barcode: string | null;
  basePrice: string;
  categoryName: string | null;
  cost: string | null;
  action: 'create' | 'update' | 'skip';
  reason?: string;
  existingId?: string;
}

type ImportMode = 'merge' | 'replace';

interface ImportPreview {
  fileName: string;
  mode: ImportMode;
  retiring: { id: string; name: string }[];
  detected: Record<string, string | null>;
  unmapped: { column: string; reason: string }[];
  totals: { rows: number; create: number; update: number; skip: number };
  newCategories: string[];
  rows: ImportRow[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Import products from the file the venue already keeps.
 *
 * Two steps on purpose. The server parses and reports what the file *would*
 * do — how many are new, how many match something already here, what it cannot
 * read and why — and only writes when that is approved. An import that half
 * lands is worse than one refused, because the operator cannot tell which half.
 *
 * The preview is sent back verbatim to commit, so what was approved is what
 * runs; a file swapped between the two steps cannot slip in.
 */
export default function ImportDialog({ open, onClose, onImported }: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();
  // Both halves of the flow — `POST /catalog/import/preview` and
  // `POST /catalog/import` — enforce `menu.update`, not `menu.create`. The
  // dialog re-states that itself so it cannot drift from whichever button
  // opened it.
  const canImport = can('menu.update');
  // ADR-0007 — the Migration Center is its own route, gated on its own
  // permission; this dialog only offers the link when it would not just 403.
  const canSeeMigrationCenter = can('imports.view');
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * `merge` adds and updates what the file names and leaves everything else.
   * `replace` treats the sheet as the whole menu and retires the rest — which
   * is what onboarding wants, and exactly what you must not do by accident.
   */
  const [mode, setMode] = useState<ImportMode>('merge');
  const [file, setFile] = useState<File | null>(null);

  const reset = useCallback(() => {
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const choose = useCallback(
    async (file: File, forMode: ImportMode = mode) => {
      // `CatalogImportService.parse` refuses an empty buffer outright. Saying so
      // here spends no round trip and points at the file rather than the upload.
      if (file.size === 0) {
        toast.error(t('catalog.import.emptyFile'));
        reset();
        return;
      }
      setBusy(true);
      try {
        const body = new FormData();
        body.append('file', file);
        body.append('mode', forMode);
        setFile(file);
        const { data } = await api.post<{ data: ImportPreview } | ImportPreview>(
          '/catalog/import/preview',
          body,
        );
        setPreview((data as { data: ImportPreview }).data ?? (data as ImportPreview));
      } catch (e) {
        toast.error(apiError(e, t('catalog.import.parseFailed')));
        reset();
      } finally {
        setBusy(false);
      }
    },
    [mode, reset, t],
  );

  const commit = useCallback(async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post('/catalog/import', preview);
      const result = (data as { data?: { created: number; updated: number } })?.data ?? data;
      toast.success(
        t('catalog.import.done', {
          created: (result as { created: number }).created,
          updated: (result as { updated: number }).updated,
        }),
      );
      reset();
      onImported();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('catalog.import.failed')));
    } finally {
      setBusy(false);
    }
  }, [onClose, onImported, preview, reset, t]);

  const tone = (action: ImportRow['action']) =>
    action === 'create' ? 'success' : action === 'update' ? 'info' : 'warning';

  /**
   * Why the server would refuse to commit, in its own terms — null when it
   * would accept. `CatalogImportService.commit` throws "Nothing to import" once
   * every row is a skip, so a preview of skips is a refusal, not an import of
   * zero rows.
   */
  const blockedReason = !canImport
    ? t('catalog.import.needsPermission')
    : !preview
      ? t('catalog.import.needsFile')
      : preview.totals.create + preview.totals.update === 0
        ? t('catalog.import.nothingToImport')
        : null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t('catalog.import.title')}
      description={t('catalog.import.subtitle')}
      className="w-full max-w-[1200px]"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-sm text-ink-muted">
            {preview
              ? t('catalog.import.summary', {
                  create: preview.totals.create,
                  update: preview.totals.update,
                  skip: preview.totals.skip,
                })
              : ''}
            {/* A disabled Import with no stated reason is its own bug. */}
            {blockedReason && preview && (
              <span className="ms-2 text-[var(--warning,#b45309)]">{blockedReason}</span>
            )}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={busy}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void commit()}
              disabled={busy || !!blockedReason}
              title={blockedReason ?? undefined}
            >
              {t('catalog.import.commit')}
            </Button>
          </div>
        </div>
      }
    >
      {busy && !preview ? (
        <LoadingState label={t('catalog.import.reading')} />
      ) : !preview ? (
        <div className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void choose(file);
            }}
          />
          <div className="flex flex-wrap gap-2">
            {(['merge', 'replace'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setMode(option);
                  if (file) void choose(file, option);
                }}
                className={`flex-1 rounded-lg border-2 p-3 text-start ${
                  mode === option
                    ? 'border-[var(--primary)] bg-[var(--muted)]'
                    : 'border-[var(--border)]'
                }`}
              >
                <span className="block text-sm font-medium">
                  {t(`catalog.import.mode.${option}`)}
                </span>
                <span className="block text-xs text-ink-muted">
                  {t(`catalog.import.mode.${option}Hint`)}
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            // The preview endpoint enforces `menu.update` too, so a reader
            // cannot even parse a file — say that instead of failing on upload.
            disabled={!canImport}
            title={canImport ? undefined : t('catalog.import.needsPermission')}
            className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-[var(--border)] p-10 hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Upload className="h-6 w-6 text-ink-muted" />
            <span className="text-sm font-medium">{t('catalog.import.choose')}</span>
            <span className="text-xs text-ink-muted">
              {canImport ? t('catalog.import.chooseHint') : t('catalog.import.needsPermission')}
            </span>
          </button>

          {/* ADR-0007 — this dialog stays the rich, Arabic-aware product
              importer it already is (sizes, per-locale columns, menu linking,
              merge/replace); it is not being taught customers, suppliers,
              inventory or opening stock. Point at the tool that already does
              those rather than leaving someone to search for it. */}
          {canSeeMigrationCenter && (
            <p className="text-xs text-ink-muted">
              {t('catalog.import.migrationCenterHint')}{' '}
              <Link to="/imports" className="text-accent underline">
                {t('catalog.import.migrationCenterCta')}
              </Link>
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {[
              { label: t('catalog.import.rows'), value: preview.totals.rows },
              { label: t('catalog.import.willCreate'), value: preview.totals.create },
              { label: t('catalog.import.willUpdate'), value: preview.totals.update },
              { label: t('catalog.import.willSkip'), value: preview.totals.skip },
            ].map((tile) => (
              <div key={tile.label} className="rounded-lg border border-[var(--border)] p-3">
                <p className="text-xs text-ink-muted">{tile.label}</p>
                <p className="mt-1 text-xl font-semibold tabular-nums">{tile.value}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-ink-muted">
            {t('catalog.import.detected')}{' '}
            {Object.entries(preview.detected)
              .filter(([, column]) => column)
              .map(([field, column]) => `${t(`catalog.import.field.${field}`)} → ${column}`)
              .join(' · ')}
          </p>

          {preview.newCategories.length > 0 && (
            <p className="text-xs text-ink-muted">
              {t('catalog.import.newCategories', { count: preview.newCategories.length })}{' '}
              {preview.newCategories.join(' · ')}
            </p>
          )}

          {/* Retiring a venue's catalog is not a number to approve; it is a
              list to read. Shown before the row table, because it is the part
              of this import that cannot be undone by re-importing. */}
          {preview.mode === 'replace' && preview.retiring.length > 0 && (
            <div className="rounded-lg border-2 border-[var(--destructive)] p-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-[var(--destructive)]" />
                {t('catalog.import.willRetire', { count: preview.retiring.length })}
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                {t('catalog.import.willRetireHint')}
              </p>
              <ul className="mt-2 max-h-32 overflow-y-auto text-xs">
                {preview.retiring.map((item) => (
                  <li key={item.id}>· {item.name}</li>
                ))}
              </ul>
            </div>
          )}

          {/* A column we read but cannot store is said out loud, not dropped. */}
          {preview.unmapped.map((column) => (
            <p
              key={column.column}
              className="flex items-start gap-2 rounded-lg bg-[var(--muted)] p-3 text-xs"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--warning,#b45309)]" />
              <span>
                <strong>{column.column}</strong> — {column.reason}
              </span>
            </p>
          ))}

          {/* Every row is rendered: this is the last look before the catalog
              changes, and a truncated preview is not something you can check. */}
          <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-[var(--border)]">
            <Table>
              <THead>
                <TR>
                  <TH>{t('catalog.import.line')}</TH>
                  <TH>{t('catalog.items.name')}</TH>
                  <TH>{t('catalog.items.barcode')}</TH>
                  {/* `catalog.items.price` does not exist — this column is the
                      sheet's price column, which already has a label. */}
                  <TH className="text-end">{t('catalog.import.field.basePrice')}</TH>
                  <TH>{t('catalog.items.category')}</TH>
                  <TH>{t('catalog.import.action')}</TH>
                </TR>
              </THead>
              <tbody>
                {preview.rows.map((row) => (
                  <TR key={row.line}>
                    <TD className="tabular-nums text-ink-muted">{row.line}</TD>
                    <TD>{row.name}</TD>
                    <TD className="tabular-nums">{row.barcode ?? '—'}</TD>
                    <TD className="text-end tabular-nums">{row.basePrice}</TD>
                    <TD>{row.categoryName ?? '—'}</TD>
                    <TD>
                      <Badge tone={tone(row.action)}>
                        {t(`catalog.import.action_${row.action}`)}
                      </Badge>
                      {row.reason && (
                        <span className="ms-2 text-xs text-ink-muted">
                          {row.reason}
                        </span>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </div>


        </div>
      )}
    </Dialog>
  );
}
